"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/features/onboarding-checklist/components/ChecklistStepAccordionItem";

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "@ospweb/sdk-ui";
import type {
  ChecklistStepItem,
  ChecklistStepMeta,
  ChecklistStepKey,
  ChecklistEntrySource,
} from "../types";
import { trackItemExpand, trackItemActionTap } from "../utils/tracking";
import { isWebNotificationBlocked } from "../utils/notificationGate";

const I18n = I18nUtil.createScoped(Translations);

export interface ChecklistStepAccordionItemProps {
  /** 步骤运行时数据 */
  stepData: ChecklistStepItem;
  /** 步骤元数据（标题/描述/按钮文案） */
  meta: ChecklistStepMeta;
  /** 是否处于展开状态 */
  isExpanded: boolean;
  /** 点击头部切换展开 */
  onToggle: () => void;
  /** 点击动作按钮 */
  onAction: (stepKey: ChecklistStepKey) => void;
  /** 埋点入口来源 */
  entrySource: ChecklistEntrySource;
  className?: string;
}

/**
 * 全屏清单页步骤行 — Figma "Step/*"（#105389 / #104392）
 * 三种状态：已完成（绿勾 + 删除线，不可点）、未完成折叠（空心圆 + chevron-right）、
 * 未完成展开（title_l + 描述 + 主按钮 + chevron-down）
 */
export const ChecklistStepAccordionItem: React.FC<
  ChecklistStepAccordionItemProps
> = ({
  stepData,
  meta,
  isExpanded,
  onToggle,
  onAction,
  entrySource,
  className,
}) => {
  const isCompleted = stepData.completed;
  const webNotificationBlocked = isWebNotificationBlocked(meta.stepKey);

  const handleHeaderClick = () => {
    if (isCompleted) {
      // 已完成项保持折叠，不触发展开
      return;
    }
    if (!isExpanded) {
      trackItemExpand(meta.stepKey);
    }
    onToggle();
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackItemActionTap(meta.stepKey, entrySource);
    onAction(meta.stepKey);
  };

  return (
    <div
      data-testid={`checklist-step-item-${meta.stepId}`}
      className={`rounded-xl ${className ?? ""}`}
    >
      {/* 头部行：圆圈 + 标题 + 指示箭头 */}
      <button
        type="button"
        onClick={handleHeaderClick}
        disabled={isCompleted}
        data-openreplay-label="checklist_step_toggle"
        className={`flex w-full items-center gap-3 rounded-xl p-3 text-left ${
          isCompleted ? "cursor-default" : "cursor-pointer"
        }`}
        aria-expanded={isExpanded}
        aria-label={`${meta.title} - ${isCompleted ? "Completed" : isExpanded ? "Expanded" : "Collapsed"}`}
      >
        {/* 状态指示圈：已完成绿勾，未完成空心圆 */}
        {isCompleted ? (
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-background_accent1_overlay100">
            <Icon name="check" size={14} className="text-white" />
          </span>
        ) : (
          <span className="size-6 shrink-0 rounded-full bg-background_white_overlay10" />
        )}

        {/* 标题：折叠 body_medium_l，展开 title_l；已完成置灰删除线 */}
        <span
          className={`min-w-0 flex-1 truncate ${
            isCompleted
              ? "body_medium_l text-content_quaternary line-through"
              : isExpanded
                ? "title_l text-content_primary"
                : "body_medium_l text-content_primary"
          }`}
        >
          {meta.title}
        </span>

        {/* 右侧指示器：已完成无箭头 */}
        {!isCompleted &&
          (isExpanded ? (
            <Icon
              name="chevron-down"
              size={24}
              className="shrink-0 text-content_tertiary"
            />
          ) : (
            <Icon
              name="chevron-right"
              size={24}
              className="shrink-0 text-content_tertiary"
            />
          ))}
      </button>

      {/* 展开区域：描述 + 行动按钮（与头部 Body 列对齐 = p-3 行内边距 + 24px 圆 + 12px 间距） */}
      <AnimatePresence initial={false}>
        {isExpanded && !isCompleted && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-3 pb-3 pl-12 pr-3">
              <p className="body_m text-content_primary">
                {webNotificationBlocked
                  ? I18n.t("Turn on notifications in the app.")
                  : meta.description}
              </p>
              {!webNotificationBlocked && (
                <button
                  type="button"
                  data-testid={`step-action-button-${meta.stepId}`}
                  data-openreplay-label="checklist_step_action"
                  onClick={handleActionClick}
                  className="inline-flex h-11 items-center justify-center self-start rounded-lg bg-background_brand_overlay100 px-4 text-white button_m"
                >
                  {meta.actionLabel}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
